import { Dialog, Transition } from "@headlessui/react";
import { Fragment, useEffect, useState } from "react";
import EntityConstants from "../../configs/EntityConstants";
import { POST, GET } from "../../service/CRUDService";
import Toast from "../../common/Toast/Toast";

export default function CreateBatchModal({
  isOpen,
  onClose,
  onBatchCreated,
  programmeCode,
  isEditMode = false,
  initialName = "",
  initialDescription = "",
  onUpdate,
}) {
  const [name, setName] = useState(initialName);
  const [description, setDescription] = useState(initialDescription);
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({
    name: "",
    description: "",
  });
  const [programmeName, setProgrammeName] = useState("");
  const [batchName, setBatchName] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  const maxCharsBatchName = 250;
  const maxCharsBatchDescription = 250;

  useEffect(() => {
    if (isEditMode) {
      setName(initialName);
      setDescription(initialDescription);
    }
  }, [isEditMode, initialName, initialDescription]);

  const validateForm = () => {
    let valid = true;
    const newErrors = {
      name: "",
      description: "",
    };

    if (!name.trim()) {
      newErrors.name = "Batch name is required";
      valid = false;
    } else if (name.length > maxCharsBatchName) {
      newErrors.name = `Batch name must be ${maxCharsBatchName} characters or less`;
      valid = false;
    }

    if (!description.trim()) {
      newErrors.description = "Description is required";
      valid = false;
    } else if (description.length > maxCharsBatchDescription) {
      newErrors.description = `Description must be ${maxCharsBatchDescription} characters or less`;
      valid = false;
    }

    setErrors(newErrors);
    return valid;
  };

  const handleNameChange = (e) => {
    const value = e.target.value;
    if (value.length <= maxCharsBatchName) {
      setName(value);
      setErrors({ ...errors, name: "" });
      setErrorMessage("");
    }
  };

  const handleDescriptionChange = (e) => {
    const value = e.target.value;
    if (value.length <= maxCharsBatchDescription) {
      setDescription(value);
      setErrors({ ...errors, description: "" });
      setErrorMessage("");
    }
  };

  const handleSubmit = () => {
    if (!validateForm()) return;

    setIsLoading(true);

    if (isEditMode) {
      onUpdate(name, description);
      setIsLoading(false);
      onClose();
      return;
    }

    const payload = {
      programme_code: programmeCode,
      batch_name: name,
      batch_description: description,
    };

    POST(EntityConstants.POST_CREATE_BATCH, payload)
      .then((response) => response.json())
      .then((data) => {
        if (data.error) {
          setErrorMessage(data);
          Toast({
            type: "error",
            title: "Error",
            message: data.error || "Failed to create batch",
          });
        } else {
          Toast({
            type: "success",
            title: "Success",
            message: "Batch created successfully",
          });
          onBatchCreated();
          resetForm();
          onClose();
        }
      })
      .catch(() => {
        Toast({
          type: "error",
          title: "Error",
          message: "Failed to create batch",
        });
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const resetForm = () => {
    setName("");
    setDescription("");
    setErrors({
      name: "",
      description: "",
    });
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const fetchProgrammeName = () => {
    GET(EntityConstants.GET_PROGRAMME_NAME)
      .then((response) => response.json())
      .then((data) => {
        if (data.programme_display_name) {
          setProgrammeName(data.programme_display_name);
        }
      })
      .catch((error) => {
        console.error("Error fetching program name:", error);
      });
  };

  const fetchBatchName = () => {
    GET(EntityConstants.GET_BATCH_NAME)
      .then((response) => response.json())
      .then((data) => {
        if (data.batch_display_name) {
          setBatchName(data.batch_display_name);
        }
      })
      .catch((error) => {
        console.error("Error fetching batch name:", error);
      });
  };

  useEffect(() => {
    fetchProgrammeName();
    fetchBatchName();
  }, []);

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={handleClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/60 backdrop-blur-md" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-lg transform overflow-hidden rounded-2xl bg-cfSecondary p-4 text-left align-middle shadow-xl transition-all">
                <div className="flex justify-between items-start">
                  <div className="w-full">
                    <Dialog.Title
                      as="h3"
                      className="text-lg font-semibold text-cfHeadingText"
                    >
                      {isEditMode ? "Edit" : "Create New"}{" "}
                      {batchName ? batchName : "Batch"}
                    </Dialog.Title>
                    <p className="text-sm text-cfGlobalText mt-1">
                      {isEditMode ? "Edit the" : "Create a new"}{" "}
                      {batchName ? batchName : "Batch"}{" "}
                      {isEditMode
                        ? `details for your ${
                            programmeName ? programmeName : "Programme"
                          } to organize templates and recipients.`
                        : `for your ${
                            programmeName ? programmeName : "Programme"
                          } to organize templates and recipients.`}
                    </p>
                    <div className="space-y-4 mt-4">
                      <div>
                        <label
                          className="block mb-2 text-sm font-medium text-cfHeadingText"
                          htmlFor="name"
                        >
                          Name
                        </label>
                        <input
                          type="text"
                          id="name"
                          className={`bg-cfTextInputBorder border ${
                            errors.name
                              ? "border-cfIssuedCredentialTemplateCardStatusFailedBG"
                              : "border-cfIssuedScreenSearchBorder"
                          } text-cfGlobalText text-sm rounded-lg focus:ring-cfButton focus:border-cfButton block p-2.5 w-full`}
                          placeholder={`Enter ${
                            batchName ? batchName : "Batch"
                          } name`}
                          value={name}
                          onChange={handleNameChange}
                        />
                        <div className="flex justify-between items-center mt-1">
                          {errors.name && (
                            <p className="text-sm text-cfIssuedCredentialTemplateCardStatusFailedBG">
                              {errors.name}
                            </p>
                          )}
                          <div className="text-right text-sm text-cfViewAllTagText ml-auto">
                            <span>
                              {maxCharsBatchName - name.length} characters left
                            </span>
                          </div>
                        </div>
                      </div>
                      <div>
                        <label
                          className="block mb-2 text-sm font-medium text-cfHeadingText"
                          htmlFor="description"
                        >
                          Description
                        </label>
                        <textarea
                          id="description"
                          rows="4"
                          className={`border ${
                            errors.description
                              ? "border-cfIssuedCredentialTemplateCardStatusFailedBG"
                              : "border-cfIssuedScreenSearchBorder"
                          } text-cfGlobalText text-md rounded-lg focus:ring-cfButton focus:border-cfButton block p-2.5 w-full resize-none`}
                          placeholder={`Enter ${
                            batchName ? batchName : "Batch"
                          } description`}
                          value={description}
                          onChange={handleDescriptionChange}
                        ></textarea>
                        <div className="flex justify-between items-center mt-1">
                          {errors.description && (
                            <p className="text-sm text-cfIssuedCredentialTemplateCardStatusFailedBG">
                              {errors.description}
                            </p>
                          )}
                          <div className="text-right text-sm text-cfViewAllTagText ml-auto">
                            <span>
                              {maxCharsBatchDescription - description.length}{" "}
                              characters left
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                    {errorMessage && (
                      <p className="mt-2 text-sm text-cfIssuedCredentialTemplateCardStatusFailedBG flex items-center">
                        <span className="mr-1 text-base leading-none">*</span>
                        {errorMessage.error} - {errorMessage.message}
                      </p>
                    )}
                  </div>

                  <button
                    type="button"
                    className="text-cfGlobalText hover:text-cfGlobalText focus:outline-none"
                    onClick={handleClose}
                  >
                    <span className="sr-only">Close</span>
                    &#10005;
                  </button>
                </div>

                <div className="flex mt-4 justify-end gap-4">
                  <button
                    type="button"
                    className="px-4 py-2 text-cfHeadingText border hover:bg-cfCardHover transition-all border-cfHeadingText rounded-[8px] focus:outline-none focus:ring-3 focus:ring-offset-2"
                    onClick={handleClose}
                    disabled={isLoading}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="px-4 py-2 bg-cfButton hover:bg-cfPrimaryDark text-cfSecondary font-semibold border transition-all border-cfHeadingText rounded-[8px] focus:outline-none focus:ring-3 focus:ring-offset-2 flex items-center justify-center min-w-[120px]"
                    onClick={handleSubmit}
                    disabled={isLoading}
                  >
                    {isLoading
                      ? isEditMode
                        ? "Updating..."
                        : "Creating..."
                      : `${isEditMode ? "Update" : "Create"} ${
                          batchName ? batchName : "Batch"
                        }`}
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}
